import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import { PageHeader } from "@/components/PageHeader";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { useCandidate } from "@/context/CandidateContext";
import { generateCoverLetter, generateSTARAnswer } from "@/server/generate-materials";
import {
  generateTailoredResume,
  type TailoredResumeResult,
} from "@/server/generate-tailored-resume";
import { supabase } from "@/integrations/supabase/client";

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

export const Route = createFileRoute("/app/generate")({
  beforeLoad: () => {
    throw redirect({ to: "/app/jobs" });
  },
  component: () => null,
});

// ---------- Sample JD ----------
const SAMPLE_JD = `Senior Frontend Engineer — Platform team

We're looking for a senior engineer to lead the next generation of our customer dashboard. You'll work in React and TypeScript, partnering with design and backend on a GraphQL API. Experience with Node.js, AWS, Docker, and modern testing (Jest) is required. Bonus: Kubernetes, Redis, accessibility-first thinking, and Figma fluency. You'll mentor mid-level engineers and own architecture for a 12-person product org.`;

// ---------- Tab Panel helper ----------
function TabPanel({ value, index, children }: { value: number; index: number; children: React.ReactNode }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

export function MaterialsPanel({
  initialJdText = "",
  hideHeader = false,
}: {
  initialJdText?: string;
  hideHeader?: boolean;
} = {}) {
  const { profile } = useCandidate();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Application materials"
          subtitle="Tailored resume, cover letter, and STAR-method Q&A drafts."
        />
      )}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<DescriptionOutlinedIcon />} iconPosition="start" label="Tailored Resume" />
            <Tab icon={<MailOutlineIcon />} iconPosition="start" label="Cover Letter" />
            <Tab icon={<QuestionAnswerOutlinedIcon />} iconPosition="start" label="Application Q&A" />
          </Tabs>
        </Box>
        <CardContent>
          <TabPanel value={tab} index={0}>
            <ResumeTab profile={profile} initialJd={initialJdText} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <CoverLetterTab initialJd={initialJdText} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <QATab profile={profile} />
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}

// ============================================================
// Tab 1 — Tailored Resume (LLM-backed)
// ============================================================
function ResumeTab({ profile, initialJd = "" }: { profile: ReturnType<typeof useCandidate>["profile"]; initialJd?: string }) {
  const [jd, setJd] = useState(initialJd);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TailoredResumeResult | null>(null);
  const quotaDialog = useQuotaError(CAPABILITIES.resume_generation);

  const hasResume =
    !!profile.resume.summary || profile.resume.chronologicalExperience.length > 0;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const headers = await getAuthHeaders();
      const res = await generateTailoredResume({
        data: { jdText: jd },
        headers,
      });
      setResult(res);
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to tailor resume.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setJd("");
    setResult(null);
    setError(null);
  };

  const formatPlain = (r: TailoredResumeResult): string => {
    const out: string[] = [];
    out.push(r.summary, "");
    for (const role of r.experience) {
      out.push(`${role.role} — ${role.company}`);
      for (const b of role.bullets) out.push(`• ${b}`);
      out.push("");
    }
    return out.join("\n");
  };

  const copyOptimized = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(formatPlain(result));
    } catch {
      // ignore
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <TextField
          label="Target job description"
          placeholder="Paste the JD here…"
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          multiline
          rows={4}
          fullWidth
          helperText="Our AI will rewrite your bullets to lead with what matters for this role — using only facts from your profile."
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={loading || jd.trim().length < 20 || !hasResume}
            onClick={handleGenerate}
          >
            {loading ? "Tailoring…" : "Generate tailored resume"}
          </Button>
          <Button variant="text" onClick={() => setJd(SAMPLE_JD)} disabled={loading}>
            Load sample JD
          </Button>
          <Button variant="text" onClick={handleReset} disabled={loading || (!jd && !result)}>
            Reset
          </Button>
        </Stack>
      </Box>

      {!hasResume && (
        <Alert severity="info">
          No resume in profile yet — head to the Profile tab to upload your resume first.
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {result && (
        <Stack spacing={2.5}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="overline" color="text.secondary">
                JD keywords
              </Typography>
              {result.detectedKeywords.length === 0 ? (
                <Typography variant="caption" color="text.disabled">None</Typography>
              ) : (
                result.detectedKeywords.map((k) => (
                  <Chip key={k} size="small" label={k} color="primary" variant="outlined" />
                ))
              )}
            </Stack>
            <IconButton size="small" onClick={copyOptimized} aria-label="Copy tailored resume">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Stack>

          {result.changeNotes.length > 0 && (
            <Alert severity="success" icon={<AutoAwesomeIcon fontSize="small" />}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">What changed</Typography>
                {result.changeNotes.map((n, i) => (
                  <Typography key={i} variant="body2">• {n}</Typography>
                ))}
              </Stack>
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="overline" color="primary">AI-optimized resume</Typography>
            <Typography variant="body1" sx={{ mt: 1.5, mb: 2.5, fontStyle: "italic" }}>
              {result.summary}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2.5}>
              {result.experience.map((role, i) => (
                <Box key={i}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {role.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {role.company}
                  </Typography>
                  <Stack spacing={0.5}>
                    {role.bullets.map((b, j) => (
                      <Typography key={j} variant="body2" sx={{ pl: 1.5 }}>
                        • {b}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>

          {result.keyMatches.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">Why you fit</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }} useFlexGap>
                {result.keyMatches.map((m, i) => (
                  <Chip key={i} label={m} color="success" variant="outlined" size="small" />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Stack>
  );
}

// ============================================================
// Tab 2 — Cover Letter
// ============================================================
const TONE_MARKS = [
  { value: 0, label: "Corporate" },
  { value: 1, label: "Startup" },
  { value: 2, label: "Creative" },
];

function CoverLetterTab({ initialJd = "" }: { initialJd?: string } = {}) {
  const [jd, setJd] = useState(initialJd);
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [tone, setTone] = useState(1);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState("");

  const canGenerate = jd.trim().length >= 20 && company.trim() && role.trim();
  const [error, setError] = useState<string | null>(null);
  const quotaDialog = useQuotaError(CAPABILITIES.cover_letter);

  const toneKey: "corporate" | "startup" | "creative" =
    tone === 0 ? "corporate" : tone === 1 ? "startup" : "creative";

  const handleGenerate = async () => {
    setLoading(true);
    setOutput("");
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const result = await generateCoverLetter({
        data: { jdText: jd, company, role, tone: toneKey },
        headers,
      });
      setOutput(result.letter);
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to generate cover letter.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
    } catch {
      // ignore
    }
  };

  return (
    <Stack spacing={3}>
      <TextField
        label="Target job description"
        placeholder="Paste the JD here…"
        value={jd}
        onChange={(e) => setJd(e.target.value)}
        multiline
        rows={3}
        fullWidth
      />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Role title"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Tone calibration
        </Typography>
        <Box sx={{ px: 1.5 }}>
          <Slider
            value={tone}
            onChange={(_, v) => setTone(v as number)}
            step={1}
            min={0}
            max={2}
            marks={TONE_MARKS}
            valueLabelDisplay="off"
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={loading || !canGenerate}
          onClick={handleGenerate}
        >
          {loading ? "Drafting…" : "Generate cover letter"}
        </Button>
        <Button variant="text" onClick={() => setJd(SAMPLE_JD)} disabled={loading}>
          Load sample JD
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ position: "relative" }}>
        <TextField
          label="Generated cover letter"
          value={output}
          multiline
          rows={12}
          fullWidth
          slotProps={{ input: { readOnly: true } }}
          placeholder="Your tailored cover letter will appear here…"
        />
        {output && (
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="Copy cover letter"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Stack>
  );
}

function buildCoverLetter({
  tone,
  company,
  role,
  topSkill,
}: {
  tone: number;
  company: string;
  role: string;
  topSkill: string;
}) {
  if (tone === 0) {
    return `Dear ${company} Hiring Team,

I am writing to express my strong interest in the ${role} position at ${company}. With over eight years of experience shipping production software, I bring deep expertise in ${topSkill} and a track record of leading high-impact initiatives across cross-functional teams.

In my most recent role, I led the rewrite of a customer-facing dashboard serving 12,000 daily users, reducing time-to-interactive by 38% and lifting NPS by 14 points. I also built and rolled out a 40-component design system adopted by four product squads — work that aligns directly with the priorities outlined in your job description.

I would welcome the opportunity to bring this same rigor and ownership to ${company}. Thank you for considering my application; I look forward to the conversation.

Sincerely,
Alex Rivera`;
  }
  if (tone === 1) {
    return `Hi ${company} crew —

I'd love to be your next ${role}. Quick version: 8+ years shipping React + TypeScript at scale, recently led a dashboard rewrite that cut TTI by 38% and bumped NPS +14. Strong in ${topSkill}, comfortable owning architecture end-to-end, and I genuinely like mentoring the people around me.

What pulled me toward ${company}: the bet you're making on the platform layer feels exactly like the kind of problem I want to spend the next chapter on. I move fast, write things down, and don't need a runway to start contributing.

Happy to jump on a call whenever works.

— Alex Rivera`;
  }
  return `${company} —

Most cover letters open with a thesis. Mine opens with a confession: I rewrite dashboards for fun.

That habit turned into an eight-year career building the kind of ${topSkill}-heavy product surfaces your ${role} posting describes. At Northwind, I led a rewrite that cut TTI by 38% and shipped a 40-component design system adopted across four squads. At Helios, I owned WCAG AA compliance across twelve patient flows because someone had to and I wanted it to be done right.

If you're looking for someone who treats craft and velocity as the same problem, let's talk.

— Alex Rivera`;
}

// ============================================================
// Tab 3 — Application Q&A
// ============================================================
function QATab({ profile }: { profile: ReturnType<typeof useCandidate>["profile"] }) {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [star, setStar] = useState<{ s: string; t: string; a: string; r: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const quotaDialog = useQuotaError(CAPABILITIES.qa_answer);

  const hasExperience = profile.resume.chronologicalExperience.length > 0;

  const handleGenerate = async () => {
    setLoading(true);
    setStar(null);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const result = await generateSTARAnswer({
        data: { question },
        headers,
      });
      setStar({
        s: result.situation,
        t: result.task,
        a: result.action,
        r: result.result,
      });
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to generate STAR answer.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!star) return;
    const text = `Situation: ${star.s}\n\nTask: ${star.t}\n\nAction: ${star.a}\n\nResult: ${star.r}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  return (
    <Stack spacing={3}>
      {!hasExperience && (
        <Alert severity="info">
          No experience in profile yet — head to the Profile tab and upload a resume or load the dummy profile.
        </Alert>
      )}

      <TextField
        label="Employer's question"
        placeholder="e.g., Tell us about a time you led a cross-functional project."
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        multiline
        rows={3}
        fullWidth
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
          disabled={loading || !hasExperience || question.trim().length < 10}
          onClick={handleGenerate}
        >
          {loading ? "Drafting…" : "Generate STAR answer"}
        </Button>
      </Stack>

      {star && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                STAR-method draft
              </Typography>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy STAR answer">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack spacing={2} divider={<Divider flexItem />}>
              <STARBlock label="Situation" color="primary" text={star.s} />
              <STARBlock label="Task" color="secondary" text={star.t} />
              <STARBlock label="Action" color="info" text={star.a} />
              <STARBlock label="Result" color="success" text={star.r} />
            </Stack>
          </CardContent>
        </Card>
      )}

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Stack>
  );
}

function STARBlock({
  label,
  color,
  text,
}: {
  label: string;
  color: "primary" | "secondary" | "info" | "success";
  text: string;
}) {
  return (
    <Box>
      <Chip label={label} size="small" color={color} sx={{ fontWeight: 700, mb: 1 }} />
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {text}
      </Typography>
    </Box>
  );
}
